<script setup lang="ts">
  import { z } from 'zod';
  import { Unpacked, UserProfile } from '@borg/types';
  import { MpSelect, pushToastMessage } from '@borg/ui';
  import { useUserLanguagesStore } from '@/layers/MyProfile/stores/userProfile.store';
  import { EditModalEvents } from './types';

  const props = defineProps<{
    isOpen: boolean;
    id?: number | string;
  }>();

  const emits = defineEmits<EditModalEvents>();

  const { t } = useI18n();
  const itemsStore = useUserLanguagesStore();

  const { data: languages } = await languagesRepository.getMany();
  const { data: languageLevels } = await languageLevelsRepository.getMany();

  const { values, state, validate, resetStates, setValues } = useForm({
    initialData: () => transformEntity(),
    schema: z.object({
      language: z.object({ id: z.string().min(1, t('REQUIRED_INPUT')), name: z.string() }),
      languageLevel: z.object({
        id: z.string().min(1, t('REQUIRED_INPUT')),
        name: z.string(),
      }),
    }),
  });

  const inProgress = computed(
    () => itemsStore.createState === 'inProgress' || itemsStore.updateState === 'inProgress',
  );

  function transformEntity(input?: Unpacked<UserProfile['languages']>) {
    return {
      language: input?.language ?? { id: '', name: '' },
      languageLevel: input?.languageLevel ?? { id: '', name: '' },
    };
  }

  function onModalOpened() {
    setValues(transformEntity(itemsStore.items.find((i) => i.id === props.id)));
  }

  async function onSubmit() {
    const valid = await validate();
    if (valid) {
      const patchValue = {
        language: values.language.id,
        languageLevel: values.languageLevel.id,
      };
      props.id
        ? await itemsStore.update(props.id, patchValue)
        : await itemsStore.create(patchValue);
      const isSuccess = props.id
        ? itemsStore.updateState === 'isSuccess'
        : itemsStore.createState === 'isSuccess';
      if (isSuccess) {
        emits('change');
        onClose();
      } else {
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.SUBMIT_ERROR_TOAST.SUBTITLE'),
        });
      }
    }
  }

  function onClose() {
    resetStates();
    emits('update:isOpen', false);
  }
</script>

<template>
  <SiteActionsModal
    :is-open="isOpen"
    :title="$t('MY_PROFILE_PAGE.LANGUAGES_MODAL.TITLE')"
    :in-progress="inProgress"
    @open="onModalOpened"
    @confirm="onSubmit"
    @cancel="onClose"
  >
    <MyProfileEditModalForm>
      <MpSelect
        v-model="values.language"
        :options="languages ?? []"
        :get-value="(i) => i.id"
        :get-label="(i) => i.name"
        :label="$t('MY_PROFILE_PAGE.LANGUAGES_MODAL.LANGUAGE_LABEL')"
        :error="state.language.message"
        searchable
        :max-menu-height="200"
        required
      />
      <MpSelect
        v-model="values.languageLevel"
        :options="languageLevels ?? []"
        :get-value="(i) => i.id"
        :get-label="(i) => i.name"
        :label="$t('MY_PROFILE_PAGE.LANGUAGES_MODAL.LANGUAGE_LEVEL_LABEL')"
        :error="state.languageLevel.message"
        required
      />
    </MyProfileEditModalForm>
  </SiteActionsModal>
</template>
